import { useState, useMemo, useEffect } from 'react';
import { FormikHelpers } from 'formik';
import {
  Add,
  InfoOutlined,
  MailOutline,
  CommentOutlined,
  Unarchive,
  Archive,
  LocalPostOffice,
  AttachEmail,
  PendingOutlined,
} from '@mui/icons-material';
import {
  ListTab,
  TabDrawerProps,
  AvatarsCell,
  TimestampCell,
  selectUser,
  Loading,
  IndexTable,
  IndexTableProps,
  MailMenuCell,
  RowCell,
  ColorTypographyCell,
} from '@centerpiece/components';
import * as Yup from 'yup';
import {
  Button,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  Badge,
  useTheme,
  Avatar as MuiAvatar,
  Box,
  Paper,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  project,
  useCreateAttachmentMutation,
  useDeleteAttachmentMutation,
  useLazyGetAttachmentQuery,
  useUpdateEventMutation,
  useGetSubscribersQuery,
  useCreateRelationshipMutation,
  useDeleteRelationshipMutation,
  useGetItemStatusesQuery,
  usePatchMessageContactMutation,
  usePatchEventMutation,
  useCreateCommentMutation,
  useGetCommentsQuery,
  usePatchCommentMutation,
  useGetEventQuery,
  useGetItemQuery,
} from 'store/api';
import {
  Action,
  Event,
  Attachment,
  ItemStatus,
  Message,
  Reaction,
  Relationship,
  Response,
  Subscriber,
  MessageContact,
  Comment,
  Item,
  ITEMSTATUSES,
  Appointment,
} from 'store/api/models';
import { getItemStatus, getStatusColorFromDate } from 'utils/status.utils';
import { useDispatch, useSelector } from 'react-redux';
import { MessageTab } from './MessageTab';
import { downloadFile } from 'utils/downloadFile.util';
import moment from 'moment';
import { ResponseTab } from './ResponseTab';

import { useLocation } from 'react-router-dom';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { ContactTooltip } from 'components/ContactTooltip';
import { useTagForm } from 'jformik/useTagForm';
import { EventTab } from './EventTab';
import { useItemContext } from 'contexts/item.context';
import { EventActualTab } from './EventActualTab';
import { CombinedEventTab } from './CombinedEventTab';
import { meetingInitialValues } from 'utils/messaging.utils';
import { AvailabilityTable } from 'components/AvailabilityTable';

export interface EventAvailabilityTabProps
  extends Omit<TabDrawerProps, 'tabs' | 'setTabs' | 'title' | 'status'> {
  id?: number;
  transparentBackdrop?: boolean;
  createProject?: Function;
  afterSave?: Function;
  template?: boolean;
  disabled?: boolean;
  breadcrumbs?: { label: string; onClick?: Function }[];
  exampleEvent?: Event;
}

export const EventAvailabilityTab = (props: EventAvailabilityTabProps) => {
  const {
    template = false,
    disabled = false,
    breadcrumbs,
    exampleEvent,
  } = props;
  const {
    id,
    item,
    itemLoading,
    availabilities,
    appointments,
    task,
    event,
    refetchItem,
    setActiveTab,
  } = useItemContext();
  const { contact: userContact } = useSelector(selectUser);
  const [composing, setComposing] = useState<boolean>(
    !availabilities?.length || availabilities?.length === 0
  );
  const [message, setMessage] = useState<Message>();
  const [response, setResponse] = useState<Response>();
  const [confirmResponse, setConfirmResponse] = useState<Response>();
  const [appointment, setAppointment] = useState<Appointment>();

  useEffect(() => {
    if (
      (!appointments?.length || appointments.length === 0) &&
      (!availabilities?.length || availabilities?.length === 0)
    ) {
      setComposing(true);
    } else {
      setComposing(false);
    }
  }, [appointments, availabilities]);

  const [patchMessageContact] = usePatchMessageContactMutation();
  const [open, setOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  if (itemLoading) {
    return <Loading />;
  } else if (composing) {
    return (
      <CombinedEventTab
        back={
          !!message || !!response || (composing && !!availabilities?.length)
        }
        onClose={() => {
          setComposing(false);
          setMessage(undefined);
          setResponse(undefined);
        }}
        onSubmit={(values: any) => {
          refetchItem();
          props?.afterSave && props?.afterSave();
        }}
      />
    );
  } else if (message || response) {
    return (
      <Paper sx={{ p: 2 }}>
        <EventTab
          back={
            !!message || !!response || (composing && !!availabilities?.length)
          }
          message={message}
          response={response}
          onClose={() => {
            setComposing(false);
            setMessage(undefined);
            setResponse(undefined);
          }}
          onSubmit={(values: any) => {
            refetchItem();
            props?.afterSave && props?.afterSave();
          }}
        />
      </Paper>
    );
  } else if (
    confirmResponse ||
    appointment ||
    (appointments?.length &&
      appointments?.length > 0 &&
      !availabilities?.length)
  ) {
    return (
      <Paper sx={{ p: 2 }}>
        <EventActualTab
          back={
            (confirmResponse || appointment) &&
            !(
              appointments?.length &&
              appointments?.length > 0 &&
              !availabilities?.length
            )
          }
          // appointment={appointment}
          confirmResponse={confirmResponse}
          onClose={() => {
            setComposing(false);
            setMessage(undefined);
            setResponse(undefined);
            setConfirmResponse(undefined);
            setAppointment(undefined);
          }}
          onSubmit={(values: any) => {
            refetchItem();
            props?.afterSave && props?.afterSave();
          }}
        />
      </Paper>
    );
  } else if (availabilities && availabilities.length > 0) {
    return (
      <>
        {availabilities?.map(m => {
          const { message, responses } = m;
          return (
            <AvailabilityTable
              {...m}
              title="Recipient Table"
              setAppointment={setAppointment}
              onRowClick={async (row: any) => {
                const msg = row.msg;
                if (msg?.contacts?.[0]?.mc_id) {
                  await patchMessageContact({
                    id: msg?.contacts?.find((mc: any) => mc.id === userContact)
                      ?.mc_id,
                    status: 'Read',
                  } as MessageContact);
                  refetchItem();

                  props?.afterSave && props?.afterSave();
                }
                setResponse(msg);
              }}
              onConfirmation={async (row: any) => {
                const msg = row.msg;
                setConfirmResponse(msg);
              }}
              heading={
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    mb: 1,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Subject:</span>{' '}
                  <i>{message.subject}</i>{' '}
                  <small>
                    (
                    <u>
                      {m.message?.form?.canceled
                        ? 'Message Canceled'
                        : `${
                            [
                              ...new Set(
                                responses?.map(response => response.contact)
                              ),
                            ].length
                          } of ${
                            message?.contacts?.filter(c => !c.author)?.length
                          } Replies Received`}
                    </u>
                    )
                  </small>
                  <Button
                    sx={{ float: 'right', textDecoration: 'underline' }}
                    color={'secondary'}
                    onClick={() => setMessage(message)}
                  >
                    View or Edit Original Message
                  </Button>
                </Typography>
              }
              headCells={[
                {
                  name: 'name',
                  label: (
                    <span>
                      Name&nbsp;<small>(status)</small>
                    </span>
                  ),
                  component: ColorTypographyCell,
                },
                {
                  name: 'availability',
                  label: 'Availability',
                  component: ColorTypographyCell,
                },
                {
                  name: 'cost',
                  label: 'Cost Total',
                  component: ColorTypographyCell,
                },
                {
                  name: 'msg',
                  disablePadding: false,
                  label: '',
                  render: rowMsg => {
                    if (rowMsg.id === 0) {
                      return (
                        <Tooltip title="Response not yet received">
                          <IconButton>
                            <PendingOutlined />
                          </IconButton>
                        </Tooltip>
                      );
                    } else {
                      const msg = rowMsg.msg;
                      const attachments = [
                        ...(msg?.attachments || []),
                        ...(msg?.actions?.map(
                          (action: Action) => action.attachments
                        ) || []),
                        ...(msg?.reactions?.map(
                          (reaction: Reaction) => reaction.reactionattachments
                        ) || []),
                      ].flat(2);
                      const newMail =
                        msg?.contacts?.filter(
                          (contact: any) =>
                            contact.status === 'New' &&
                            contact.id === userContact
                        )?.length > 0;
                      return (
                        <Tooltip title="Click to view message">
                          <IconButton
                            onClick={async () => {
                              if (msg.msgType === 'message') {
                                setMessage(msg);
                              } else {
                                if (msg?.contacts?.[0]?.mc_id) {
                                  await patchMessageContact({
                                    id: msg?.contacts?.find(
                                      (mc: any) => mc.id === userContact
                                    )?.mc_id,
                                    status: 'Read',
                                  } as MessageContact);
                                  refetchItem();

                                  props?.afterSave && props?.afterSave();
                                }
                                setResponse(msg);
                              }
                            }}
                          >
                            {attachments.length > 0 ? (
                              <Badge
                                badgeContent={newMail ? 1 : 0}
                                variant="dot"
                                color={'secondary'}
                              >
                                <AttachEmail />
                              </Badge>
                            ) : (
                              <Badge
                                badgeContent={newMail ? 1 : 0}
                                variant="dot"
                                color={'secondary'}
                              >
                                <LocalPostOffice />
                              </Badge>
                            )}
                          </IconButton>
                        </Tooltip>
                      );
                    }
                  },
                },
              ]}
              rows={[
                ...responses?.map(
                  (response: Response) =>
                    (({
                      id: response.id,
                      name: {
                        text: !!response?.contact_r?.firstname
                          ? `${response?.contact_r.firstname} ${response?.contact_r.lastname}`
                          : response?.contact_r.email,
                        subtext: '(Replied)',
                      },
                      availability: {
                        text: [
                          ...(response?.form?.body
                            ?.find((b: any) => b.type === 'date_range')
                            ?.action?.daterange?.reply?.map(
                              (r: any) =>
                                `${moment(r.start).format(
                                  'MM/DD/YY'
                                )} - ${moment(r.end).format('MM/DD/YY')}`
                            ) || []),
                          response?.form?.body
                            ?.find((b: any) => b.type === 'date_range')
                            ?.action?.daterange?.suggest?.map(
                              (r: any) =>
                                `${moment(r.start).format(
                                  'MM/DD/YY'
                                )} - ${moment(r.end).format('MM/DD/YY')}`
                            ) || [],
                        ].join('\r\n'),
                      },
                      cost: {
                        text: `$ ${
                          response?.form?.body
                            ?.find((b: any) => b.type === 'cost_breakdown')
                            ?.action?.custom_inputs?.reduce(
                              (
                                accumulator: number,
                                currentValue: {
                                  custom_input: { value: string };
                                }
                              ) => {
                                return (
                                  accumulator +
                                  parseInt(
                                    currentValue?.custom_input?.value || '0'
                                  )
                                );
                              },
                              0
                            ) | 0
                        }`,
                        tooltip: response?.form?.body
                          ?.find((b: any) => b.type === 'cost_breakdown')
                          ?.action?.custom_inputs?.reduce(
                            (
                              accumulator: string,
                              currentValue: {
                                custom_input: { label: string; value: string };
                              }
                            ) => {
                              var res = '';
                              if (accumulator !== '') {
                                res = accumulator + ',\n';
                              }
                              res =
                                res +
                                `${currentValue?.custom_input?.label}: ${currentValue?.custom_input?.value}`;
                              return res;
                            },
                            ''
                          ),
                      },
                      msg: response,
                    } as RowCell) || ([] as RowCell[]))
                ),
                ...message.contacts
                  .filter(
                    c =>
                      !c.author && !responses.find(r => r.contact_r.id === c.id)
                  )
                  .map(c => ({
                    id: 0,
                    name: {
                      text: !!c.firstname
                        ? `${c.firstname} ${c.lastname}`
                        : c.email,
                      subtext: `(${
                        c.status === 'New' ? 'Not Read' : c.status
                      })`,
                    },
                  })),
              ]}
            />
          );
        })}
        <Box sx={{ mt: 15 }}></Box>
        <SpeedDial
          ariaLabel=""
          sx={{
            position: 'fixed',
            right: 0,
            bottom: 56,
            /**
             * Width should be drawer width
             */
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
          icon={<SpeedDialIcon />}
          onClose={handleClose}
          onOpen={handleOpen}
          open={open}
        >
          <SpeedDialAction
            key={'Add Message'}
            icon={<MailOutline />}
            tooltipTitle={'Add Message'}
            tooltipOpen
            onClick={() => {
              setComposing(true);
              handleClose();
            }}
          />
        </SpeedDial>
      </>
    );
  } else {
    return <Loading />;
  }
};

const getChipColorFromStatus = (status: string) => {
  switch (status) {
    case 'Approved':
    case 'Approved with Markups':
    case 'approved':
    case 'Confirmed':
    case 'confirmed':
      return 'secondary';
    case 'Call Me':
    case 'call-me':
      return 'warning';
    case 'Rejected':
    case 'rejected':
    case 'Not Ready':
    case 'not-ready':
      return 'error';
    default:
      return 'default';
  }
};

// const AvailabilityTable = ({
//   title,
//   heading,
//   onRowClick,
//   rows,
//   headCells,
//   onConfirmation,
//   setAppointment,
// }: {
//   heading: any;
//   onConfirmation: Function;
//   setAppointment: Function;
// } & IndexTableProps) => {
//   const { refetchItem, afterSave, item, patchItem } = useItemContext();
//   const [selected, setSelected] = useState<readonly number[]>(
//     item?.appointments?.length
//       ? item.appointments?.map(a => a.form?.selected)
//       : []
//   );
//   const disabled =
//     item?.appointments?.length &&
//     item.appointments?.map(a => a.form?.selected)?.length > 0;
//   const contact_r = useSelector(selectUser);
//   const [patchMessageContact] = usePatchMessageContactMutation();
//   useEffect(() => {
//     if (item?.appointments?.length) {
//       setSelected(prev => [
//         ...prev,
//         ...(item.appointments?.map(a => a.form?.selected) || []),
//       ]);
//     }
//   }, item?.appointments);
//   return (
//     <Paper elevation={3} sx={{ p: 2, mb: 5 }}>
//       {heading}
//       <IndexTable
//         title={title}
//         // defaultOrder={'desc'}
//         // defaultOrderBy={'due'}
//         onRowClick={onRowClick}
//         checkboxes={true}
//         headCells={headCells}
//         rows={
//           disabled
//             ? rows.map(r => ({ ...r, disabled: !r.form?.selected }))
//             : rows
//         }
//         selected={selected}
//         setSelected={setSelected}
//         selectMultiple={false}
//         // iconHead={<div />}
//         pagination={false}
//       />
//       <Box sx={{ textAlign: 'center', mt: 2 }}>
//         {item?.appointments?.length ? (
//           <Button
//             sx={{ textAlign: 'center', px: 3, py: 1 }}
//             color={'secondary'}
//             variant="contained"
//             onClick={() => setAppointment(item?.appointments?.[0])}
//           >
//             View Event Notice
//           </Button>
//         ) : (
//           <Button
//             variant="contained"
//             color={'secondary'}
//             sx={{ textAlign: 'center', px: 3, py: 1 }}
//             disabled={!!!selected?.length}
//             onClick={async () => {
//               let newMsgs: number[] = [];
//               rows.forEach(row => {
//                 const mc_id = row.msg?.contacts?.find(
//                   (mc: any) => mc.id === contact_r.contact && mc.status === 'New'
//                 )?.mc_id;
//                 if (mc_id) newMsgs.push(mc_id);
//               });
//               await Promise.all(
//                 newMsgs.map(newMsg =>
//                   patchMessageContact({
//                     id: newMsg,
//                     status: 'Read',
//                   } as MessageContact)
//                 )
//               );
//               refetchItem();

//               await afterSave();
//               onConfirmation(rows.find(r => r.id === selected[0]));
//             }}
//           >
//             Confirm Selection
//           </Button>
//         )}
//         {/* <FormControlLabel
//           sx={{ ml: 2 }}
//           value="rejection"
//           control={<Checkbox />}
//           label="Send rejection"
//           labelPlacement="end"
//         /> */}
//       </Box>
//     </Paper>
//   );
// };
